import React, { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Typography,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Checkbox,
  Chip,
} from '@mui/material'
import {
  Search,
  Clear,
  Star,
  Category,
  Download,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  SelectAll,
  Deselect,
} from '@mui/icons-material'
import AnnotationCard from './AnnotationCard'
import ItemsPerPageSelector from '../ItemsPerPageSelector'
import Pagination from '../Pagination'
import { openDocumentInTab } from '../../services/tabManager'
import {
  exportAnnotationsToCSV,
  exportSelectedAnnotations,
} from './annotationExportUtils'
import apiService from '../../services/ApiService'
const AnnotationsSection = ({
  annotations,
  loading,
  searchQuery,
  filters,
  onSearchChange,
  onFiltersChange,
  onSearch,
  onDelete,
  onUpdate,
  onOpen,
  totalItems,
  currentPage,
  itemsPerPage,
  totalPages,
  onPageChange,
  onItemsPerPageChange,
  showPagination,
  isMobile,
  themeColors,
  showSnackbar,
  readOnly = false,
  exportOptions = {},
}) => {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery)
  const [exporting, setExporting] = useState(false)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedAnnotations, setSelectedAnnotations] = useState(new Set())

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    onSearch(localSearchQuery)
  }

  const handleClearSearch = () => {
    setLocalSearchQuery('')
    onSearchChange('')
    onSearch('')
  }

  const handleClearFilters = () => {
    onFiltersChange({
      category: 'all',
      priority: 'all',
      isImportant: 'all',
      tags: [],
    })
  }

  const handleOpenAnnotation = useCallback(
    (annotation) => {
      // Don't open in selection mode
      if (selectionMode) return
      try {
        // Construct URL based on document type
        let baseUrl = ''
        if (annotation.documentType === 'article') {
          baseUrl = `/articles/${annotation.documentId}`
        } else if (annotation.documentType === 'guide') {
          baseUrl = `/guidances/${annotation.documentId}`
        } else if (annotation.documentType === 'decision') {
          baseUrl = `/decisions/${annotation.documentId}`
        } else if (annotation.documentType === 'tax-treaty') {
          baseUrl = `/tax-treaties/${annotation.documentId}`
        } else if (annotation.documentType === 'blog') {
          baseUrl = `/blogs/${annotation.documentId}`
        } else {
          baseUrl = `/documents/${annotation.documentId}`
        }

        // Create a unique hash for the annotation
        const annotationHash = `annotation-${annotation.id}`
        const fullUrl = `${window.location.origin}${baseUrl}#${annotationHash}`

        // Use tab manager to open/focus the document
        openDocumentInTab(
          fullUrl,
          annotation.selectedText?.substring(0, 50) + '...',
          showSnackbar
        )

        if (showSnackbar) {
          showSnackbar('Opening annotation in document...', 'info')
        }
      } catch (error) {
        console.error('Error opening annotation:', error)
        if (showSnackbar) {
          showSnackbar('Failed to open annotation', 'error')
        }
      }
    },
    [showSnackbar, selectionMode]
  )
  // Toggle selection mode
  const handleToggleSelectionMode = () => {
    setSelectionMode(!selectionMode)
    setSelectedAnnotations(new Set())
  }

  // Toggle individual annotation selection
  const handleToggleAnnotation = (annotationId) => {
    setSelectedAnnotations((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(annotationId)) {
        newSet.delete(annotationId)
      } else {
        newSet.add(annotationId)
      }
      return newSet
    })
  }

  // Select all on current page
  const handleSelectAllPage = () => {
    const pageAnnotationIds = safeAnnotations.map((a) => a.id)
    setSelectedAnnotations((prev) => {
      const newSet = new Set(prev)
      pageAnnotationIds.forEach((id) => newSet.add(id))
      return newSet
    })
  }

  // Deselect all
  const handleDeselectAll = () => {
    setSelectedAnnotations(new Set())
  }

  // Export selected
  const handleExportSelected = async () => {
    try {
      setExporting(true)

      if (showSnackbar) {
        showSnackbar('Preparing CSV export...', 'info')
      }

      // Export all annotations with current filters
      const count = await exportSelectedAnnotations(
        apiService,
        selectedAnnotations,
        filters,
        searchQuery,
        exportOptions,
      )

      if (showSnackbar) {
        showSnackbar(
          `Successfully exported ${count} annotation${
            count !== 1 ? 's' : ''
          } to CSV!`,
          'success'
        )
      }
      // Exit selection mode after export
      setSelectionMode(false)
      setSelectedAnnotations(new Set())
    } catch (error) {
      console.error('Export failed:', error)
      if (showSnackbar) {
        showSnackbar(error.message || 'Failed to export annotations', 'error')
      }
    } finally {
      setExporting(false)
    }
  }
  // Safety check: ensure annotations is always an array
  const safeAnnotations = React.useMemo(() => {
    if (!annotations) return []
    if (Array.isArray(annotations)) return annotations
    if (annotations.content && Array.isArray(annotations.content)) {
      return annotations.content
    }
    return []
  }, [annotations])
  // Group annotations by importance and date
  const groupedAnnotations = React.useMemo(() => {
    const important = safeAnnotations.filter((a) => a.isImportant) // This assumes annotations is always an array
    const regular = safeAnnotations.filter((a) => !a.isImportant)

    // Sort each group by date (newest first)
    const sortByDate = (a, b) => {
      const dateA = new Date(
        Date.UTC(
          ...a.createdAt
            .slice(0, 6)
            .map((val, idx) => (idx === 1 ? val - 1 : val))
        )
      )
      const dateB = new Date(
        Date.UTC(
          ...b.createdAt
            .slice(0, 6)
            .map((val, idx) => (idx === 1 ? val - 1 : val))
        )
      )
      return dateB - dateA
    }

    return {
      important: important.sort(sortByDate),
      regular: regular.sort(sortByDate),
    }
  }, [safeAnnotations])

  // Clear selection when page changes
  useEffect(() => {
    if (selectionMode) {
      // Keep selections but user needs to be aware of multi-page selection
    }
  }, [currentPage])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={isMobile ? 30 : 40} />
        <Typography sx={{ ml: 2 }} variant={isMobile ? 'body2' : 'body1'}>
          Loading annotations...
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent='end'
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        {/* <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 600 }}>
          My Annotations ({totalItems})
        </Typography> */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {/* Export/Selection Buttons */}
          {totalItems > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {!selectionMode ? (
                <Button
                  variant='outlined'
                  startIcon={<CheckBoxIcon />}
                  onClick={handleToggleSelectionMode}
                  sx={{
                    borderColor: '#232536',
                    color: '#232536',
                    '&:hover': {
                      borderColor: '#232536',
                      bgcolor: '#ffcf51',
                      color: '#232536',
                    },
                  }}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  Select To Export
                </Button>
              ) : (
                <>
                  <Button
                    variant='contained'
                    startIcon={
                      exporting ? (
                        <CircularProgress size={16} color='inherit' />
                      ) : (
                        <Download />
                      )
                    }
                    onClick={handleExportSelected}
                    disabled={selectedAnnotations.size === 0 || exporting}
                    sx={{
                      bgcolor: '#232536',
                      color: 'white',
                      '&:hover': {
                        bgcolor: '#3a3e5a',
                      },
                      '&.Mui-disabled': {
                        bgcolor: '#9e9e9e',
                      },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    }}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    {exporting ? 'Exporting...' : 'Export CSV'}
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={handleToggleSelectionMode}
                    sx={{
                      borderColor: '#dc2626',
                      color: '#dc2626',
                      '&:hover': {
                        borderColor: '#b91c1c',
                        backgroundColor: '#fee2e2',
                      },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    }}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          )}
          <Stack
            direction='row'
            spacing={1.5}
            sx={{
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {/* Category Filter */}
            <FormControl
              size='small'
              sx={{
                minWidth: { xs: 100, sm: 140 },
                flex: { xs: 1, sm: '0 0 auto' },
              }}
            >
              <InputLabel
                sx={{
                  '&.Mui-focused': {
                    color: '#232536',
                  },
                }}
              >
                Category
              </InputLabel>
              <Select
                value={filters.category}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    category: e.target.value,
                  })
                }
                label='Category'
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#232536',
                  },
                }}
                // MenuProps={{
                //   PaperProps: {
                //     sx: {
                //       '& .MuiMenuItem-root': {
                //         '&:hover': {
                //           backgroundColor: '#f5f5f5',
                //         },
                //         '&.Mui-selected': {
                //           backgroundColor: '#e8e8e8',
                //           '&:hover': {
                //             backgroundColor: '#d5d5d5',
                //           },
                //         },
                //       },
                //     },
                //   },
                // }}
              >
                <MenuItem value='all'>All</MenuItem>
                <MenuItem value='interpretation'>Interpretation</MenuItem>
                <MenuItem value='example'>Example</MenuItem>
                <MenuItem value='question'>Question</MenuItem>
              </Select>
            </FormControl>
            {/* Items Per Page Selector */}
            <ItemsPerPageSelector
              itemsPerPage={itemsPerPage}
              setItemsPerPage={onItemsPerPageChange}
              loading={loading}
              themeColors={themeColors}
              // showLabel={false}
            />
          </Stack>
        </Stack>
      </Stack>
      {/* Selection Mode Actions */}
      {selectionMode && safeAnnotations.length > 0 && (
        <Stack
          direction='row'
          spacing={1}
          sx={{ mb: 2 }}
          flexWrap='wrap'
          alignItems='center'
        >
          <Chip
            label={`${selectedAnnotations.size} selected`}
            color='primary'
            size={isMobile ? 'small' : 'medium'}
            sx={{
              bgcolor: '#ffda1b',
              color: '#232536',
              fontWeight: 600,
            }}
          />
          <Button
            size='small'
            startIcon={<SelectAll />}
            onClick={handleSelectAllPage}
            variant='filled'
            sx={{ fontSize: '0.75rem', minWidth: { xs: 'auto', sm: '120px' } }}
          >
            Select Page
          </Button>
          <Button
            size='small'
            startIcon={<Deselect />}
            onClick={handleDeselectAll}
            variant='filled'
            sx={{ fontSize: '0.75rem', minWidth: { xs: 'auto', sm: '120px' } }}
          >
            Deselect All
          </Button>
        </Stack>
      )}
      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <form onSubmit={handleSearchSubmit}>
          <TextField
            fullWidth
            placeholder='Search annotations, content, or tags...'
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
              endAdornment: localSearchQuery && (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={handleClearSearch}
                    size='small'
                    sx={{ mr: 1 }}
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: { xs: 1.5, md: 2 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover fieldset': {
                  borderColor: '#232536',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#232536',
                },
              },
            }}
          />
        </form>
      </Box>

      {/* Annotations List */}
      {safeAnnotations.length > 0 ? (
        <>
          {/* Important Annotations */}
          {groupedAnnotations.important.length > 0 && (
            <>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Star sx={{ color: '#ffda1b' }} />
                <Typography
                  variant={isMobile ? 'subtitle1' : 'h6'}
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: '#232536',
                  }}
                >
                  Important Annotations ({groupedAnnotations.important.length})
                </Typography>
              </Stack>

              {groupedAnnotations.important.map((annotation) => (
                <Box
                  key={annotation.id}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    mb: 2,
                  }}
                >
                  {selectionMode && (
                    <Checkbox
                      checked={selectedAnnotations.has(annotation.id)}
                      onChange={() => handleToggleAnnotation(annotation.id)}
                      sx={{
                        mt: 1,
                        color: '#232536',
                        '&.Mui-checked': {
                          color: '#ffda1b',
                        },
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <AnnotationCard
                      annotation={annotation}
                      onDelete={onDelete}
                      onUpdate={onUpdate}
                      onOpen={handleOpenAnnotation}
                      isMobile={isMobile}
                      showSnackbar={showSnackbar}
                      selectionMode={selectionMode}
                      readOnly={readOnly}
                    />
                  </Box>
                </Box>
              ))}

              {groupedAnnotations.regular.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Stack
                    direction='row'
                    alignItems='center'
                    spacing={1}
                    sx={{ mb: 2 }}
                  >
                    <Category sx={{ color: '#666' }} />
                    <Typography
                      variant={isMobile ? 'subtitle1' : 'h6'}
                      sx={{ fontWeight: 600, mb: 2, color: '#232536' }}
                    >
                      Other Annotations ({groupedAnnotations.regular.length})
                    </Typography>
                  </Stack>
                </>
              )}
            </>
          )}

          {/* Regular Annotations */}
          {groupedAnnotations.regular.map((annotation) => (
            <Box
              key={annotation.id}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              {selectionMode && (
                <Checkbox
                  checked={selectedAnnotations.has(annotation.id)}
                  onChange={() => handleToggleAnnotation(annotation.id)}
                  sx={{
                    mt: 1,
                    color: '#232536',
                    '&.Mui-checked': {
                      color: '#ffda1b',
                    },
                  }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <AnnotationCard
                  annotation={annotation}
                  onDelete={onDelete}
                  onUpdate={onUpdate}
                  onOpen={handleOpenAnnotation}
                  isMobile={isMobile}
                  showSnackbar={showSnackbar}
                  selectionMode={selectionMode}
                  readOnly={readOnly}
                />
              </Box>
            </Box>
          ))}

          {/* Pagination */}
          {showPagination && (
            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              currentPage={currentPage}
              paginate={onPageChange}
            />
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
          <Typography
            variant={isMobile ? 'subtitle1' : 'h6'}
            color='text.secondary'
            sx={{ mb: 2 }}
          >
            {localSearchQuery || filters.category !== 'all'
              ? 'No annotations match your criteria'
              : 'No annotations created yet'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {localSearchQuery || filters.category !== 'all'
              ? 'Try adjusting your search terms or filters'
              : 'Start highlighting and annotating articles to see them here!'}
          </Typography>
          {(localSearchQuery || filters.category !== 'all') && (
            <Button
              onClick={() => {
                handleClearSearch()
                handleClearFilters()
              }}
              sx={{
                mt: 2,
                borderColor: '#232536',
                color: '#232536',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#3a3e5a',
                  color: '#3a3e5a',
                },
              }}
              variant='outlined'
            >
              Clear Search & Filters
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

export default AnnotationsSection
